/**
 * Development-only permission overrides, supplied by the Aspire AppHost.
 *
 * A user's SupportCo permissions reach the SPA only inside the B2C id token, which is minted at
 * sign-in by B2C calling `/api/v1/Claims/sign-in` against whichever environment's database that
 * policy points at. Running locally that is deployed dev, not the harness database — so a
 * permission that exists only in the harness database never reaches the browser, and the UI hides a
 * feature the local API would happily serve. `REACT_APP_DEV_PERMISSIONS` (a comma-separated list of
 * permission names, injected by `AddSupportCoWeb`) names those permissions so a local run's UI
 * agrees with its own API.
 *
 * This grants nothing. The API resolves permissions from the harness database itself via
 * HarnessClaimsTransformer, and enforcement always reads the token
 * (SupportCoProvider.CheckAccountHasPermission) — so a name listed here that the API does not
 * recognise simply produces a 403 instead of a hidden feature.
 *
 * Inert outside development, and not merely by convention: CRA substitutes `process.env.NODE_ENV`
 * at build time, so in a production bundle the guard below is a constant and the rest of the
 * function is dropped. The list cannot widen a deployed build even if the variable is set.
 *
 * See aspire/README.md § "Permissions in a local run".
 */
export const getDevPermissions = (): string[] => {
  if (process.env.NODE_ENV !== 'development') return [];

  return (process.env.REACT_APP_DEV_PERMISSIONS ?? '')
    .split(',')
    .map(name => name.trim())
    .filter(name => name.length > 0);
};

/**
 * Unions the development overrides onto the permissions carried by the token.
 *
 * Only ever adds — the token stays authoritative — so the result is identical when the two agree,
 * and identical to the token alone outside development. Token order is preserved so anything
 * reading the list positionally is unaffected.
 */
export const withDevPermissions = (fromToken: string[] = []): string[] => {
  const devPermissions = getDevPermissions();
  if (devPermissions.length === 0) return fromToken;

  return Array.from(new Set([...fromToken, ...devPermissions]));
};
